import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FeedbackResponse } from '../../types/feedback.types';

interface FeedbackTableProps {
  feedbackEntries: FeedbackResponse[];
}

const TYPE_LABELS: Record<string, string> = {
  onboarding: 'ONBOARDING',
  post_search_survey: 'POST-SEARCH',
  feedback: 'FEEDBACK',
  bug: 'BUG',
};

const TYPE_COLORS: Record<string, string> = {
  onboarding: 'bg-purple-100 text-purple-700',
  post_search_survey: 'bg-blue-100 text-blue-700',
  feedback: 'bg-green-100 text-green-700',
  bug: 'bg-red-100 text-red-700',
};

const FeedbackTable: React.FC<FeedbackTableProps> = ({ feedbackEntries }) => {
  const navigate = useNavigate();

  if (!feedbackEntries || !Array.isArray(feedbackEntries) || feedbackEntries.length === 0) {
    return <p className="text-sm text-gray-600">No feedback entries found.</p>;
  }

  const handleRowClick = (userId: number | null) => {
    if (userId !== null) {
      navigate(`/admin/users/${userId}`);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Content
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
              User
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              Created At
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {feedbackEntries.map((feedback) => {
            const isClickable = feedback.user_id !== null;
            return (
              <tr
                key={feedback.id}
                onClick={() => handleRowClick(feedback.user_id)}
                className={isClickable ? 'cursor-pointer hover:bg-indigo-50' : undefined}
                title={isClickable ? 'View user profile' : undefined}
              >
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {feedback.id}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      TYPE_COLORS[feedback.type] ?? 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {TYPE_LABELS[feedback.type] ?? feedback.type.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-700 max-w-sm whitespace-normal break-words">
                  {feedback.content}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700 hidden sm:table-cell">
                  {feedback.user_email ?? (feedback.user_id ? `#${feedback.user_id}` : 'Anonymous')}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700 hidden md:table-cell">
                  {new Date(feedback.created_at).toLocaleDateString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FeedbackTable;
